import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import CustomAlert from "./CustomAlert";
import { createPortal } from "react-dom";

const REACT_FE = process.env.REACT_APP_FE_BASE

function Alerts({ clientId, onStatusUpdate, }) {
  const [pastAlerts, setPastAlerts] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);


  const [activeAlerts, setActiveAlerts] = useState([]); // array of alerts
  const playingAudiosRef = useRef({}); // key: alertId, value: Audio object

  const shownAlertsRef = useRef(new Set()); // Track shown alert IDs
  const [limit, setLimit] = useState("20");
  const limitChangeRef = useRef(false);


  const firstLoadRef = useRef(true);

  const [audioUnlocked, setAudioUnlocked] = useState(false);

  useEffect(() => {
    const unlockAudio = () => {
      setAudioUnlocked(true);
      window.removeEventListener("click", unlockAudio);
      console.log("🔓 Audio playback unlocked by user");
    };
    window.addEventListener("click", unlockAudio);
    return () => window.removeEventListener("click", unlockAudio);
  }, []);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        if (!clientId) {
          console.warn("⚠️ No clientId provided to Alerts component.");
          return;
        }

        const limitParam = limit === "All" ? "" : `&limit=${limit}`;
        const res = await axios.get(`${REACT_FE}/api/alerts?clientId=${clientId}${limitParam}`);

        setPastAlerts(res.data);

        const statusMap = {};
        res.data.forEach(alert => {
          if (["device disconnected", "back online"].includes(alert.alertType)) {
            statusMap[alert.deviceID] = alert.alertType;
          }
        });

        onStatusUpdate && onStatusUpdate(statusMap);
      } catch (err) {
        console.error("❌ Failed to fetch past alerts:", err.message);
      }
    };

    fetchAlerts(); // First fetch

    const interval = setInterval(fetchAlerts, 5000);
    return () => clearInterval(interval);
  }, [clientId, limit]); // ✅ Now reacts to limit changes



  useEffect(() => {
    if (!pastAlerts || pastAlerts.length === 0) return;

    const allAlerts = ["red", "orange", "yellow", "error", "device disconnected", "back online", "solar battery disconnected", "solar battery reconnected"];
    const popupAlerts = ["red", "orange", "yellow"];

    // 🛡️ 1. On first load, just track all without triggering popups
    if (firstLoadRef.current) {
      pastAlerts.forEach(alert => {
        if (alert && alert._id) {
          shownAlertsRef.current.add(alert._id);
        }
      });
      firstLoadRef.current = false;
      return;
    }

    // 🛡️ 2. If user just changed limit, absorb alerts without popup
    if (limitChangeRef.current) {
      pastAlerts.forEach(alert => {
        if (alert && alert._id) {
          shownAlertsRef.current.add(alert._id);
        }
      });
      limitChangeRef.current = false;
      return;
    }

    // ✅ 3. Otherwise, this is a real-time update – show unseen popup alerts
    pastAlerts.forEach(latestAlert => {
      if (!latestAlert || !latestAlert._id) return;

      const { _id, deviceName, alertType, distance } = latestAlert;

      if (!allAlerts.includes(alertType)) return;
      if (shownAlertsRef.current.has(_id)) return;

      shownAlertsRef.current.add(_id);

      if (popupAlerts.includes(alertType)) {
        setActiveAlerts(prev => [
          ...prev,
          {
            id: _id,
            deviceName,
            type: alertType,
            message:
              alertType === "error"
                ? "Temp and Hum sensor failure"
                : `${alertType.toUpperCase()} Alert: Water level at ${distance} cm`
          }
        ]);
      }
    });
  }, [pastAlerts]);





  useEffect(() => {
    if (!audioUnlocked) return;

    const alertLevels = ["red", "orange", "yellow"];
    const sounds = {
      red: "/alertRED.mp3",
      orange: "/alertORANGE.mp3",
      yellow: "/alertYELLOW.mp3"
    };

    activeAlerts.forEach(alert => {
      const { id, type } = alert;

      if (!alertLevels.includes(type)) return; // Only play sound for red/orange/yellow
      if (playingAudiosRef.current[id]) return; // Already playing this sound

      const audio = new Audio(sounds[type]);
      audio.loop = true;

      audio.play()
        .then(() => {
          playingAudiosRef.current[id] = audio;
        })
        .catch(err => {
          console.error(`🔇 Failed to play ${type} sound:`, err);
        });
    });

  }, [audioUnlocked, activeAlerts]); // ✅ Notice: depends on audioUnlocked and activeAlerts

  const getBackgroundColor = (alertType) => {
    switch (alertType) {
      case "red":
        return "#6c1e1e"; // dark red
      case "orange":
        return "#704214"; // dark orange
      case "yellow":
        return "#8b8000"; // dark yellow
      default:
        return "transparent"; // normal for others
    }
  };


  const filteredAlerts = selectedFilter
    ? pastAlerts.filter(alert => alert.alertType === selectedFilter)
    : pastAlerts;

  return (
    <div style={{
      backgroundColor: "#151C23",
      color: "white",
      borderRadius: "12px",
      width: "100%",
      padding: "20px",
      maxHeight: "calc(100vh - 160px)",
      overflowY: "auto",
       overscrollBehavior: "contain",
    }}>

      <div
        className="d-flex justify-content-between align-items-center mb-4"
      >
        <div className="d-flex align-items-center gap-3">
          <h3 className="fw-bold text-white mb-0">Alerts</h3>

          {/* Label + Dropdown */}
          <div className="d-flex align-items-center gap-2">
            <span style={{ color: "#ccc", fontSize: "14px", marginTop: "10px" }}>Set limit:</span>
            <select
              value={limit}
              onChange={(e) => {
                limitChangeRef.current = true;
                setLimit(e.target.value);
              }}
              style={{
                backgroundColor: "#222",
                color: "#fff",
                border: "1px solid #555",
                borderRadius: "6px",
                padding: "4px 8px",
                marginTop: "2px",
                fontSize: "12px"
              }}
            >
              {[20, 30, 40, 50, "All"].map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ position: "relative" }}>
          <FontAwesomeIcon
            icon={faFilter}
            onClick={() => setDropdownVisible(!dropdownVisible)}
            style={{ color: "#fff", cursor: "pointer" }}
          />
          {dropdownVisible && (
            <div style={{
              position: "absolute",
              top: "30px",
              right: 0,
              backgroundColor: "#222",
              border: "1px solid #444",
              borderRadius: "6px",
              zIndex: 10,
              width: "160px"
            }}>
              {["red", "orange", "yellow", "error", "device disconnected", "back online", "solar battery disconnected", "solar battery reconnected", "reset"].map(type => (
                <div
                  key={type}
                  onClick={() => {
                    setSelectedFilter(type === "reset" ? null : type);
                    setDropdownVisible(false);
                  }}
                  style={{
                    padding: "8px 12px",
                    color: "#fff",
                    fontSize: "14px",
                    cursor: "pointer",
                    borderBottom: "1px solid #333"
                  }}
                >
                  {type === "reset" ? "Reset Filter" : `Show ${type.charAt(0).toUpperCase() + type.slice(1)}`}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>


      <div className="alert-scrollbar" style={{
        backgroundColor: "#101419",
        padding: "12px",
        borderRadius: "10px",
        color: "white",
        maxHeight: "500px",
        overflowY: "auto"
      }}>
        {filteredAlerts.length === 0 ? (
          <div className="text-center text-muted">
            <p>No past alerts found.</p>
          </div>
        ) : (
          filteredAlerts
            .slice(0, limit === "All" ? filteredAlerts.length : parseInt(limit))
            .map((alert, index) => (

              <div key={index} style={{
                padding: "10px",
                borderBottom: "1px solid #333",
                backgroundColor: getBackgroundColor(alert.alertType)
              }}>
                <div className="d-flex justify-content-between">
                  {/* 🛠️ Now showing deviceName */}
                  <strong>{alert.deviceName || "Unknown Device"}</strong>
                  <span style={{ color: "#bbb", fontSize: "0.85rem" }}>
                    {new Date(alert.triggeredAt).toLocaleString()}
                  </span>
                </div>
                <div style={{ color: "#aaa" }}>
                  {alert.alertType === "error" ? (
                    <strong>Temp and Hum sensor failure</strong>
                  ) : (
                    <>
                      <div>
                        Alert: <strong style={{ textTransform: "capitalize" }}>{alert.alertType}</strong>
                      </div>

                      {["red", "orange", "yellow"].includes(alert.alertType) && alert.distance != null && (
                        <div>
                          Distance: <strong>{alert.distance} cm</strong>
                        </div>
                      )}
                      {["device disconnected", "back online"].includes(alert.alertType) && (
                        <>
                          <div>
                            {alert.distance != null && (
                              <>Last distance: <strong>{alert.distance} cm</strong> | </>
                            )}
                            {alert.solarVoltage != null && (
                              <>Last solar voltage: <strong>{alert.solarVoltage} V</strong></>
                            )}
                          </div>
                          {alert.batteryVoltage != null && (
                            <div>
                              Last battery voltage: <strong>{alert.batteryVoltage} V</strong>
                            </div>
                          )}
                        </>
                      )}

                      {["solar battery disconnected", "solar battery reconnected"].includes(alert.alertType) && alert.solarVoltage != null && (
                        <div>
                          Solar voltage: <strong>{alert.solarVoltage} V</strong>
                        </div>
                      )}
                    </>
                  )}

                </div>
              </div>
            ))
        )}
      </div>


      {createPortal(
        <div className="custom-alert-container">
          {activeAlerts.map(alert => (
            <CustomAlert
              key={alert.id}
              alertData={alert}
              onStop={() => {
                setActiveAlerts(prev => prev.filter(a => a.id !== alert.id));
                if (playingAudiosRef.current[alert.id]) {
                  playingAudiosRef.current[alert.id].pause();
                  playingAudiosRef.current[alert.id].currentTime = 0;
                  delete playingAudiosRef.current[alert.id];
                }
              }}
            />
          ))}
        </div>,
        document.body
      )}

    </div>
  );
}

export default Alerts;
